import { Button } from '@/components/ui/button'

/**
 * The blocking screen for the client-skew floor (design §5.3; architecture
 * §8). Rendered by `AppShell` in place of its normal nav/content whenever
 * `pwa/skew.ts`'s store is flipped — by EITHER of that file's two triggers,
 * the boot/focus poll finding this build below the server's advertised
 * floor, or a live command failing with 426 `client_upgrade_required`
 * mid-session. Both converge on this SAME component (`pwa/skew.ts`'s own
 * top comment states why that convergence, not two different screens, is
 * the point): a client old enough to be refused is told the same thing and
 * given the same way out regardless of which trigger caught it first.
 *
 * `window.location.reload()`, not a client-side `navigate(...)` — same
 * reasoning `shell/ErrorBoundary.tsx`'s `Fallback` already states for the
 * identical choice: a full reload re-fetches `index.html` and every asset
 * fresh, re-running every module's top-level code from scratch, which is
 * the only way a client stuck on a stale build actually PICKS UP the new
 * one. A router navigation keeps the same already-loaded (and, by
 * definition here, rejected) JS in memory — it would move the URL without
 * changing a single byte of what's actually running.
 *
 * Styled like `NotFoundScreen`/`Fallback` — the same "something's wrong,
 * here's the one way back" family of full-screen states, not a one-off.
 *
 * `flex-1`, not `min-h-screen`: `AppShell` renders this INSIDE its own
 * `min-h-dvh` column, below `<UpdateBanner />`, so a full-viewport minimum
 * here is a second viewport on top of whatever the banner used — the same
 * fix item 12b applied one level up, and the same one the `<nav>` above it
 * needed. `NotFoundScreen`/`Fallback` keep `min-h-screen` correctly: they
 * are top-level route elements with no such column around them.
 */
export function UpgradeRequired() {
  return (
    <div className="mx-auto flex max-w-sm flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-xl font-semibold">Update required</h1>
      <p className="text-sm text-muted-foreground">
        This app has been updated since you last loaded it. Reload to get the latest version before
        continuing.
      </p>
      <Button onClick={() => window.location.reload()}>Reload</Button>
    </div>
  )
}
